'use client';

import { Eye, EyeOff } from 'lucide-react';
import * as React from 'react';
import { flushSync } from 'react-dom';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

interface PasswordInputProps extends Omit<React.ComponentProps<'input'>, 'type'> {
  /**
   * Used inside the toggle button's aria-label as
   *   "Show <toggleLabel>" / "Hide <toggleLabel>"
   * Default is the literal word "password" — override on forms with
   * multiple password fields so screen-reader users can tell them
   * apart (e.g. "new password" vs "confirm password").
   */
  toggleLabel?: string;
}

/**
 * Password input with a show/hide toggle. Drop-in replacement for
 * `<Input type="password" />` — same API minus `type`.
 *
 * Accessibility:
 *   - The toggle button is keyboard-focusable (no negative tabindex)
 *     per GOV.UK / USWDS reveal-password guidance.
 *   - aria-label + aria-pressed reflect the current state.
 *   - Per-instance `toggleLabel` lets forms distinguish two password
 *     fields for screen readers.
 *
 * Autofill safety:
 *   - On form submit, the input is forced back to `type="password"`
 *     before submission completes. Password managers and browser
 *     autofill rely on the `type` attribute to recognize the field;
 *     leaving it as `type="text"` at submit time can cause the value
 *     to be saved as a regular text entry instead of as a password.
 */
function PasswordInput({ className, toggleLabel = 'password', ...props }: PasswordInputProps) {
  const [visible, setVisible] = React.useState(false);
  const wrapperRef = React.useRef<HTMLDivElement>(null);

  // Force the input back to type=password on form submission so password
  // managers see a real password field at the moment of submit. Plain
  // setVisible(false) inside a DOM submit handler only queues a React
  // update — the type attribute wouldn't actually flip until React's
  // next commit, by which time the submit event has already propagated.
  // flushSync forces the state update + DOM mutation synchronously, so
  // the input has type="password" before any form-submit listener
  // downstream (or password manager) inspects it.
  React.useEffect(() => {
    const form = wrapperRef.current?.closest('form');
    if (!form) return;
    const handle = () => {
      flushSync(() => setVisible(false));
    };
    form.addEventListener('submit', handle);
    return () => form.removeEventListener('submit', handle);
  }, []);

  return (
    <div ref={wrapperRef} className="relative">
      <Input type={visible ? 'text' : 'password'} className={cn('pr-9', className)} {...props} />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        aria-label={visible ? `Hide ${toggleLabel}` : `Show ${toggleLabel}`}
        aria-pressed={visible}
        className="absolute inset-y-0 right-0 flex items-center pr-2 text-neutral-500 hover:text-neutral-900 focus-visible:text-neutral-900 focus-visible:outline-none"
      >
        {visible ? (
          <EyeOff className="h-4 w-4" aria-hidden="true" />
        ) : (
          <Eye className="h-4 w-4" aria-hidden="true" />
        )}
      </button>
    </div>
  );
}

export { PasswordInput };
